import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Premium enterprise page header — eyebrow + title + meta + actions.
 * Backward compatible with the older API (title + subtitle + right).
 *
 * `backTo` (Wave 3 Phase C AXIS B — in-app Back navigation):
 *   `<PageHeader backTo={{ label: "Back to Tours", to: "/tours" }} … />`
 *   or a smart form using previous-location context:
 *   `<PageHeader backTo={{ label: "Back", to: "/tours", fallbackTo: "/tours" }} … />`
 *   Renders an ArrowLeft + label pill above the title. Uses React Router
 *   Link — never `window.history.back()` — so it works even when the user
 *   arrived via a deep link or the browser history is out of sync.
 *
 * If the router-provided `location.state.from` is present and points at a
 * known internal route, we prefer it over `to` (owner rule: smart back
 * returns to origin context when known; Customer→Contract → Back to
 * Customer; Tour→Contract → Back to Tour). Otherwise falls back to `to`.
 */
export const PageHeader = ({
  title,
  subtitle,
  right,
  actions,
  eyebrow,
  meta,
  testid,
  backTo,
}) => {
  const actionSlot = actions || right;
  const description = meta || subtitle;
  const location = useLocation();
  let backHref = null;
  let backLabel = null;
  if (backTo) {
    backLabel = backTo.label || "Back";
    const fromState = location?.state?.from;
    // Only trust `fromState` when it looks like a same-origin path.
    if (typeof fromState === "string" && fromState.startsWith("/")) {
      backHref = fromState;
    } else if (typeof backTo === "string") {
      backHref = backTo;
    } else {
      backHref = backTo.to || backTo.fallbackTo || null;
    }
  }
  return (
    <div
      className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 md:gap-6 mb-5 pb-4"
      style={{ borderBottom: "1px solid var(--border-subtle)" }}
      data-testid={testid}
    >
      <div className="min-w-0">
        {backHref && (
          <Link
            to={backHref}
            data-testid="page-back"
            className="inline-flex items-center gap-1 mb-2 text-[12px] font-medium tracking-tight"
            style={{ color: "var(--brand-blue)" }}
          >
            <ArrowLeft size={13} />
            <span className="hover:underline">{backLabel}</span>
          </Link>
        )}
        {eyebrow && (
          <div
            data-testid="page-eyebrow"
            className="text-[10.5px] uppercase tracking-[0.1em] font-semibold mb-1.5"
            style={{ color: "var(--fg-muted)" }}
          >
            {eyebrow}
          </div>
        )}
        <h1
          className="font-display text-[22px] md:text-[24px] leading-tight tracking-tight break-words"
          style={{ color: "var(--fg-primary)" }}
        >
          {title}
        </h1>
        {description && (
          <div className="mt-1.5 text-[13px]" style={{ color: "var(--fg-secondary)" }}>
            {description}
          </div>
        )}
      </div>
      {actionSlot && (
        <div className="shrink-0 flex flex-col md:flex-row md:items-center gap-2 [&_button]:w-full md:[&_button]:w-auto [&_a]:w-full md:[&_a]:w-auto">
          {actionSlot}
        </div>
      )}
    </div>
  );
};

export const PageShell = ({ children }) => (
  <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4 md:py-8 overflow-x-hidden">
    {children}
  </div>
);
